import { getUserData } from "@/auth/getUserData";
import { useInboxModuleType } from "@/composables/v2.1/useInboxModuleType";
import useUnreadChats from "@/composables/v2.1/useUnreadChats";
// @ts-ignore
import toastificationContent from "@/layouts/components/toastification/toastificationContent.vue";
import axios from "@/libs/axios";
import i18n from "@/libs/i18n";
import { useChatsStore } from "@/store/useChatsStore";
import { useTeamsStore } from "@/store/useTeamsStore";
import { Assignee, ChatDto, ChatsListChat } from "@saysimple-platform/dtos";
import { ref } from "@vue/composition-api";
import { useToast } from "vue-toastification/composition";

const isAssignPending = ref(false);

export default function useAssignChat() {
    const { publicApiEndpointPath } = useInboxModuleType();
    const chatsStore = useChatsStore();
    const toast = useToast();
    const { updateUnreadChat } = useUnreadChats();

    const assign = async (chatsListChat: ChatsListChat, assignee: Assignee): Promise<ChatDto> => {
        const chat = chatsStore.getChatsListChatById(chatsListChat.id);
        // @note double == instead of triple, because differences in possibly null and undefined.
        if (
            chat !== undefined &&
            chat.assignedUserId == assignee.user?.id &&
            chat.assignedTeamId == assignee.team?.id
        ) {
            return;
        }

        isAssignPending.value = true;

        if (chat !== undefined) {
            updateUnreadChat({
                chatId: chat.id,
                status: chat.status,
                assignedUserId: assignee.user?.id,
                assignedTeamId: assignee.team?.id,
                isChatRead: Boolean(chat.read),
            });
        }

        return axios
            .post<ChatDto>(`${publicApiEndpointPath.value}/assign`, {
                chatId: chatsListChat.id,
                userId: assignee.user?.id ?? null,
                teamId: assignee.team?.id ?? null,
            })
            .then(({ data }) => {
                // We have to this here, because we may need to switch the status filter what results in a new backend fetch,
                // so the new data have to be already in the database in that case.
                assignee = chatsStore.setAssignee(chatsListChat.id, chatsListChat.contactId, assignee);

                return data;
            })
            .catch((error) => {
                toast({
                    component: toastificationContent,
                    props: {
                        title: i18n.t("entities.conversation.Assign"),
                        text: i18n.t("entities.conversation.could-not-assign"),
                        icon: "times",
                        variant: "error",
                    },
                });

                return error;
            })
            .finally(() => {
                isAssignPending.value = false;
            });
    };

    const unassign = (chat: ChatsListChat): Promise<ChatDto> => {
        isAssignPending.value = true;

        updateUnreadChat({
            chatId: chat.id,
            status: chat.status,
            assignedUserId: null,
            assignedTeamId: null,
            isChatRead: Boolean(chat.read),
        });

        return axios
            .post<ChatDto>(`${publicApiEndpointPath.value}/unassign`, { chatId: chat.id })
            .then((response) => {
                chatsStore.setAssignee(chat.id, chat.contactId);

                return response.data;
            })
            .catch((error) => {
                toast({
                    component: toastificationContent,
                    props: {
                        title: i18n.t("entities.conversation.Assign"),
                        text: i18n.t("entities.conversation.could-not-assign"),
                        icon: "times",
                        variant: "error",
                    },
                });

                return error;
            })
            .finally(() => {
                isAssignPending.value = false;
            });
    };

    // You can pass an chatId, so you can also assign not-active chats from the chats list to you directly (context menu for example).
    const assignToMe = (
        chatsListChat: ChatsListChat,
        options: { executeBackendRequest: boolean } = { executeBackendRequest: true }
    ): Assignee => {
        if (!chatsListChat.id) {
            throw new Error("No chatId passed");
        }

        // You can assign the active chat, but also a chat from the chats list without opening it via context menu.
        // So first try to find it from the chats list, if not found (because youre on a different status filter), then check the active chat.
        const chat = chatsStore.getChatsListChatById(chatsListChat.id) ?? chatsStore.activeChat.chat;
        if (!chat) {
            throw new Error(`Cannot find chat with id ${chatsListChat.id} to assign.`);
        }

        //----------------------------------------------------------------------

        const { isUserPartOfTeam, getRandomUserTeamForChannel } = useTeamsStore();
        const teamId = isUserPartOfTeam(getUserData().id, chat.assignedTeamId)
            ? chat.assignedTeamId
            : getRandomUserTeamForChannel(getUserData().id, chat.channelId);

        const assignee: Assignee = {
            user: { id: getUserData().id },
            team: {
                id: teamId ?? null,
            },
        };

        // "Send message" also calls this function to keep assign to me behavior consistent, but in that case,
        // the backend will assign already, so we don't need to execute a separate backend request to assign.
        if (options.executeBackendRequest) {
            assign(chatsListChat, assignee).catch(() => null);
        }

        return assignee;
    };

    return {
        assign,
        unassign,
        assignToMe,
        isAssignPending,
    };
}
